/**
 * CSV primitives shared by every export (the payout ledger on `/payouts`, a tenant's subscriber
 * statement): plain decimals in the row's currency, RFC-4180 quoting, 0028's formula-injection
 * guard on text cells (H §9.5), and the browser download.
 */
import { minorUnitDigits } from '@/i18n/format';

export const CSV_BOM = '\uFEFF';

/** Decimal string in the given currency — always `.`, never locale-formatted. */
export function minorToDecimal(minor: number, currency: string): string {
  const digits = minorUnitDigits(currency, 'en');
  const sign = minor < 0 ? '-' : '';
  const abs = Math.abs(minor);
  if (digits === 0) return `${sign}${abs}`;
  const divisor = 10 ** digits;
  const whole = Math.trunc(abs / divisor);
  const frac = String(abs % divisor).padStart(digits, '0');
  return `${sign}${whole}.${frac}`;
}

/**
 * Spreadsheet formula-injection guard (H §9.5): a TEXT cell starting `=`, `+`, `-`, `@`, tab or
 * CR is prefixed with `'` so a spreadsheet shows it as text. Never applied to the amount
 * columns — `-` also starts a negative number, which must stay a number.
 */
export function guardFormula(value: string): string {
  return /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
}

export function csvField(value: string): string {
  if (/[",\n\r]/.test(value)) return `"${value.replaceAll('"', '""')}"`;
  return value;
}

export function downloadCsv(filename: string, text: string): void {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
